import { useServices, useToast } from '@/app/providers';
import { Button, Input, Modal, Select } from '@/core/ui/components';
import { useBillingPlans } from '@/modules/billing/ui/hooks/useBilling';
import type { OnboardTenantResponse, TenantAdmin } from '@yawi/types';
import { useMemo, useState, type FormEvent } from 'react';
import { TenantCreatedModal } from './TenantCreatedModal';

interface FormData {
  name: string;
  slug: string;
  planId: string;
  adminEmail: string;
  adminName: string;
  settings: {
    timezone: string;
    language: string;
    currency: string;
  };
}

interface TenantFormModalProps {
  tenant?: TenantAdmin;
  /** Plan de la suscripción vigente; en edición es solo lectura (se cambia en Billing). */
  planName?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function TenantFormModal({ tenant, planName, onClose, onSuccess }: TenantFormModalProps) {
  const { tenantRepository } = useServices();
  const { showToast } = useToast();
  const { plans, isLoading: plansLoading } = useBillingPlans();

  const isEdit = !!tenant;

  // Map plans to Select options
  const planOptions = useMemo(() => {
    return plans.map((p) => ({
      value: p.id,
      label: `${p.name} - $${Number(p.basePrice).toFixed(0)}/${p.billingCycle === 'YEARLY' ? 'year' : 'mo'}`,
    }));
  }, [plans]);

  // Find initial planId - for existing tenant, we'd need to look it up
  // For now, use first available plan or empty string (sortOrder 2 is typically "popular")
  const initialPlanId = useMemo(() => {
    if (plans.length === 0) return '';
    const popular = plans.find((p) => p.sortOrder === 2);
    return popular?.id || plans[0]?.id || '';
  }, [plans]);

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: tenant?.name || '',
    slug: tenant?.slug || '',
    planId: initialPlanId,
    adminEmail: '',
    adminName: '',
    settings: {
      timezone: tenant?.settings?.timezone || 'America/Mexico_City',
      language: tenant?.settings?.language || 'es',
      currency: tenant?.settings?.currency || 'MXN',
    },
  });
  const [onboardResult, setOnboardResult] = useState<OnboardTenantResponse | null>(null);

  // Update planId when plans load
  useMemo(() => {
    if (initialPlanId && !formData.planId) {
      setFormData((prev) => ({ ...prev, planId: initialPlanId }));
    }
  }, [initialPlanId, formData.planId]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (!isEdit) {
      if (!formData.adminEmail?.trim()) {
        newErrors.adminEmail = 'Admin email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
        newErrors.adminEmail = 'Invalid email format';
      }

      if (!formData.adminName?.trim()) {
        newErrors.adminName = 'Admin name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (isEdit) {
        await tenantRepository.update(tenant.id, {
          name: formData.name,
          settings: formData.settings,
        });
        showToast('success', 'Tenant actualizado correctamente');
        onSuccess();
      } else {
        // Use onboard() for new tenants
        const result = await tenantRepository.onboard({
          name: formData.name,
          slug: formData.slug,
          planId: formData.planId,
          adminName: formData.adminName,
          adminEmail: formData.adminEmail,
          settings: formData.settings,
        });
        // Show credentials modal
        setOnboardResult(result);
        showToast('success', 'Tenant creado exitosamente');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : `Error al ${isEdit ? 'actualizar' : 'crear'} tenant`;
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlugChange = (value: string) => {
    // Auto-generate slug from name
    const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setFormData((prev) => ({ ...prev, slug }));
  };

  const handleCloseSuccessModal = () => {
    setOnboardResult(null);
    onSuccess();
  };

  // If we have onboard result, show the credentials modal
  if (onboardResult) {
    return (
      <TenantCreatedModal
        isOpen={true}
        onClose={handleCloseSuccessModal}
        result={onboardResult}
      />
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Editar Tenant' : 'Crear Tenant'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {isEdit ? 'Actualizar' : 'Crear'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tenant Info */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-theme-secondary uppercase tracking-wider">
            Información del Tenant
          </h4>

          <Input
            label="Nombre"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              if (!isEdit && !formData.slug) {
                handleSlugChange(e.target.value);
              }
            }}
            error={errors.name}
            placeholder="Residencial Bonaterra"
          />

          <Input
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value.toLowerCase() }))}
            error={errors.slug}
            placeholder="bonaterra"
            disabled={isEdit}
          />
          {!isEdit && (
            <p className="text-xs text-theme-tertiary -mt-2">
              URL del tenant: {formData.slug || 'tu-slug'}.hommi.mx
            </p>
          )}

          {isEdit ? (
            <div>
              <p className="block text-sm font-medium text-theme-secondary mb-1.5">Plan</p>
              <p className="text-theme">{planName ?? 'Sin suscripción'}</p>
              <p className="text-xs text-theme-tertiary mt-1">
                El plan se cambia desde la pestaña Billing del tenant.
              </p>
            </div>
          ) : (
            <Select
              label="Plan"
              value={formData.planId}
              onChange={(e) => setFormData((prev) => ({ ...prev, planId: e.target.value }))}
              options={planOptions}
              disabled={plansLoading || planOptions.length === 0}
            />
          )}
          {!isEdit && !plansLoading && planOptions.length === 0 && (
            <p className="text-xs text-amber-600 -mt-2">
              No hay planes disponibles. Crea un plan en Billing primero.
            </p>
          )}
        </div>

        {/* Admin Info - Only for create */}
        {!isEdit && (
          <div className="space-y-4 pt-4 border-t border-theme">
            <h4 className="text-sm font-medium text-theme-secondary uppercase tracking-wider">
              Cuenta de Administrador
            </h4>

            <Input
              label="Nombre del Admin"
              value={formData.adminName || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, adminName: e.target.value }))}
              error={errors.adminName}
              placeholder="Eduardo Hernández"
            />

            <Input
              label="Email del Admin"
              type="email"
              value={formData.adminEmail || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))}
              error={errors.adminEmail}
              placeholder="admin@example.com"
            />

            <p className="text-xs text-theme-tertiary">
              Se generará una contraseña automáticamente y se mostrará después de crear el tenant.
            </p>
          </div>
        )}

        {/* Settings */}
        <div className="space-y-4 pt-4 border-t border-theme">
          <h4 className="text-sm font-medium text-theme-secondary uppercase tracking-wider">
            Configuración
          </h4>

          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Zona Horaria"
              value={formData.settings?.timezone || 'America/Mexico_City'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, timezone: e.target.value },
                }))
              }
              options={[
                { value: 'America/Mexico_City', label: 'México' },
                { value: 'America/New_York', label: 'New York' },
                { value: 'America/Los_Angeles', label: 'Los Angeles' },
                { value: 'America/Denver', label: 'Denver' },
                { value: 'Europe/London', label: 'Londres' },
                { value: 'Europe/Madrid', label: 'Madrid' },
              ]}
            />

            <Select
              label="Idioma"
              value={formData.settings?.language || 'es'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, language: e.target.value },
                }))
              }
              options={[
                { value: 'es', label: 'Español' },
                { value: 'en', label: 'English' },
              ]}
            />

            <Select
              label="Moneda"
              value={formData.settings?.currency || 'MXN'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  settings: { ...prev.settings, currency: e.target.value },
                }))
              }
              options={[
                { value: 'MXN', label: 'MXN' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
              ]}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
